/**
 * DemocratCharts — useCandles hook.
 *
 * Fetches OHLCV for (symbol, exchange, timeframe, days) and exposes
 * loading/error state. Re-fetches when params change. Cancels in-flight
 * requests on unmount so a quick timeframe switch doesn't race.
 */

import { useEffect, useState, useRef } from "react";
import { AxiosError } from "axios";
import { api } from "@/lib/api";

export interface Candle {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface CandleResponse {
  symbol: string;
  exchange: string;
  timeframe: string;
  source: "yahoo" | "cache" | "kite" | "mixed" | "empty" | "cache_partial";
  count: number;
  candles: Candle[];
}

interface UseCandlesParams {
  symbol: string;
  exchange: string;
  timeframe: string;
  days: number;
  enabled?: boolean;
}

interface UseCandlesResult {
  data: CandleResponse | null;
  loading: boolean;
  error: string | null;
}

export function useCandles({
  symbol,
  exchange,
  timeframe,
  days,
  enabled = true,
}: UseCandlesParams): UseCandlesResult {
  const [data, setData] = useState<CandleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !symbol) return;

    const id = ++reqIdRef.current;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const resp = await api.get<CandleResponse>("/candles", {
          params: { symbol, exchange, timeframe, days },
        });
        // Ignore if a newer request has been fired in the meantime
        if (id === reqIdRef.current) {
          setData(resp.data);
          setLoading(false);
        }
      } catch (e) {
        if (id !== reqIdRef.current) return;
        const ae = e as AxiosError<{ detail?: string }>;
        setError(ae.response?.data?.detail || ae.message || "Failed to fetch candles");
        setLoading(false);
      }
    })();
  }, [symbol, exchange, timeframe, days, enabled]);

  return { data, loading, error };
}
